import { useEffect, useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { useTranslation } from '../../../application/hooks/useTranslation';
import {
  avatarFailureOf,
  useChangeAvatar,
  type AvatarFailure,
} from '../../../application/hooks/useProfileAvatar';
import {
  AvatarPrepareError,
  prepareAvatar,
} from '../../../domain/avatar/prepareAvatar';
import { Button } from '../ui/Button';

export interface AvatarChangeDialogProps {
  open: boolean;
  onClose: () => void;
  onCooldownServer?: (failure: AvatarFailure) => void;
}

export function AvatarChangeDialog({ open, onClose, onCooldownServer }: AvatarChangeDialogProps) {
  const { t } = useTranslation();
  const changeAvatar = useChangeAvatar();

  const [preparedFile, setPreparedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const inFlight = useRef(false);

  // Capture trigger element for focus return on close
  useEffect(() => {
    if (open) {
      triggerElementRef.current = document.activeElement as HTMLElement | null;
      // Focus first element or close button
      const timer = setTimeout(() => {
        if (!modalRef.current) return;
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length > 0) focusable[0].focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      triggerElementRef.current?.focus();
      triggerElementRef.current = null;
    }
  }, [open]);

  // Clean up object URLs when unmounting or changing preview
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!open) return null;

  const resetState = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreparedFile(null);
    setErrorMsg(null);
    setSuccessMsg(null);
    setIsPreparing(false);
    setIsSubmitting(false);
    inFlight.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const processFile = async (file: File) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    setIsPreparing(true);

    try {
      const prepared = await prepareAvatar(file);
      setPreparedFile(prepared);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(prepared);
      setPreviewUrl(url);
    } catch (err) {
      setPreparedFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);

      if (err instanceof AvatarPrepareError) {
        if (err.code === 'TOO_LARGE') {
          setErrorMsg(t('profile.avatar.errors.inputTooLarge'));
        } else if (err.code === 'UNSUPPORTED_TYPE') {
          setErrorMsg(t('profile.avatar.errors.unsupported'));
        } else if (err.code === 'CORRUPT') {
          setErrorMsg(t('profile.avatar.errors.corrupt'));
        } else if (err.code === 'EMPTY') {
          setErrorMsg(t('profile.avatar.errors.empty'));
        } else {
          setErrorMsg(t('profile.avatar.errors.corrupt'));
        }
      } else {
        setErrorMsg(t('profile.avatar.errors.corrupt'));
      }
    } finally {
      setIsPreparing(false);
    }
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void processFile(file);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void processFile(file);
  };

  const handleSave = async () => {
    if (!preparedFile || isSubmitting || inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      await changeAvatar.mutateAsync(preparedFile);
      setSuccessMsg(t('profile.avatar.success'));
      // upload success resets state
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setPreparedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const failure = avatarFailureOf(err);
      if (failure.code === 'AVATAR_CHANGE_COOLDOWN' || failure.status === 429) {
        onCooldownServer?.(failure);
        const nextDate = failure.nextChangeAt ? new Date(failure.nextChangeAt).toLocaleString() : '';
        setErrorMsg(t('profile.avatar.errors.cooldown', { date: nextDate }));
      } else if (failure.code === 'AVATAR_TOO_LARGE' || failure.status === 413) {
        setErrorMsg(t('profile.avatar.errors.tooLarge'));
      } else if (failure.code === 'AVATAR_INVALID_TYPE') {
        setErrorMsg(t('profile.avatar.errors.unsupported'));
      } else if (failure.code === 'ACCOUNT_SUSPENDED' || failure.status === 403) {
        setErrorMsg(t('profile.avatar.errors.suspended'));
      } else if (failure.code === 'STALE_UPDATE' || failure.status === 409) {
        setErrorMsg(t('profile.avatar.errors.stale'));
      } else if (!failure.status) {
        setErrorMsg(t('profile.avatar.errors.network'));
      } else {
        setErrorMsg(t('profile.avatar.errors.generic'));
      }
    } finally {
      setIsSubmitting(false);
      inFlight.current = false;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (!isSubmitting) {
        e.stopPropagation();
        handleClose();
      }
      return;
    }
    if (e.key === 'Tab') {
      if (!modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={handleClose}>
      <div
        ref={modalRef}
        className="modal avatar-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="avatar-dialog-title"
        aria-busy={isSubmitting}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        data-testid="avatar-change-dialog"
      >
        <header className="modal-header">
          <h2 id="avatar-dialog-title" className="modal-title">
            {t('profile.avatar.dialogTitle')}
          </h2>
          <button
            className="tap modal-close"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label={t('profile.avatar.close')}
            data-testid="avatar-dialog-close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="modal-body avatar-dialog-body">
          <p className="avatar-dialog-desc">{t('profile.avatar.dialogDescription')}</p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={onFileInputChange}
            data-testid="avatar-file-input"
          />

          <div
            className={`avatar-dropzone ${isDragging ? 'is-dragging' : ''}`}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            tabIndex={0}
            role="button"
            aria-label={t('profile.avatar.dropzone')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            data-testid="avatar-dropzone"
          >
            <Upload size={32} className="avatar-dropzone-icon" />
            <span className="avatar-dropzone-text">
              {isDragging ? t('profile.avatar.dropzoneActive') : t('profile.avatar.dropzone')}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              data-testid="avatar-browse-btn"
            >
              {t('profile.avatar.browse')}
            </Button>
          </div>

          {previewUrl && (
            <div className="avatar-preview-section" data-testid="avatar-preview-section">
              <span className="avatar-preview-label">{t('profile.avatar.preview')}</span>
              <div className="avatar-preview-container">
                <img
                  src={previewUrl}
                  alt={t('profile.avatar.preview')}
                  className="avatar-preview-image"
                  data-testid="avatar-preview-img"
                />
              </div>
            </div>
          )}

          {errorMsg && (
            <div role="alert" className="avatar-error-alert" data-testid="avatar-error">
              {errorMsg}
            </div>
          )}

          {successMsg && (
            <div role="status" className="avatar-success-status" data-testid="avatar-success">
              {successMsg}
            </div>
          )}

          <div className="avatar-dialog-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid="avatar-cancel-btn"
            >
              {t('profile.avatar.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void handleSave()}
              disabled={!preparedFile || isSubmitting || isPreparing}
              loading={isSubmitting}
              data-testid="avatar-save-btn"
            >
              {isSubmitting ? t('profile.avatar.saving') : t('profile.avatar.save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
